import React, { useCallback, useEffect, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { Channel, UserResponse } from 'stream-chat'
import { ChannelPreviewUIComponentProps, ChatContextValue, useChatContext } from 'stream-chat-react'
import { useUnreadContext } from 'components/Chat/contexts/UnreadContext'
import type { StreamChatGenerics } from 'components/Chat/types'
import { getChannelName, getInitials } from 'components/Chat/helper'
import 'components/Chat/components/MessagingChannelPreview/MessagingChannelPreview.css'
import isEmpty from 'lodash/isEmpty'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'

type Props = ChannelPreviewUIComponentProps & {
  channel: Channel
  searchChannel: string
  usersAssessmentResponseByQuestion?: any
  chatUnreadMessageCountAction: any
  setShowChannelPreview?: any
  showChannelCreate?: boolean
  setShowChannelCreate: Dispatch<SetStateAction<boolean>>
  setActiveChannel?: ChatContextValue['setActiveChannel']
  channelMatchingMessages?: Map<string, any>
  isSearching?: boolean
}

type ExtendedResponse = UserResponse & {
  firstName?: string
  lastName?: string
}

type ExtendedUserResponse<T extends StreamChatGenerics> = UserResponse<T> & ExtendedResponse

const MessagingChannelPreview = (props: Props) => {
  const {
    channel,
    setActiveChannel: propsSetActiveChannel,
    showChannelCreate,
    setShowChannelCreate,
    unread,
    searchChannel,
    channelMatchingMessages,
    isSearching,
  } = props
  const {
    channel: activeChannel,
    client,
    setActiveChannel: contextSetActiveChannel,
  } = useChatContext<StreamChatGenerics>()
  const { channelMessageCount, setChatsUnreadCount, setChannelMessageCount } = useUnreadContext()
  const [members, setMembers]: any = useState([])

  // Use setActiveChannel from props or context
  const setActiveChannel = propsSetActiveChannel || contextSetActiveChannel

  const isActiveChannel = channel?.id === activeChannel?.id
  const [isChannelDisplay, setIsChannelDisplay] = useState(true)

  const channelMessageCounts = !isEmpty(channelMessageCount)
    ? channelMessageCount?.[channel.cid]
    : 0

  useEffect(() => {
    const findMembers = Object.values(channel.state.members).filter(
      ({ user }) => user?.id !== client.userID
    )
    if (findMembers) {
      setMembers(findMembers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUnreadCounts = () => {
    if (unread) {
      setChatsUnreadCount((chatsUnreadCount) => chatsUnreadCount - unread)
    }
  }

  const removeUnreadMessage = () => {
    setChannelMessageCount((state) => ({
      ...state,
      [channel.cid]: 0,
    }))
  }

  const searchByUserId = (): boolean => {
    for (const userId in channel?.state?.members) {
      let searchQuery = searchChannel.toLowerCase()
      let name: string = ''

      const user: ExtendedUserResponse<StreamChatGenerics> | undefined =
        channel.state.members[userId]?.user
      if (user?.name) {
        name = user.name.toLowerCase()
        searchQuery = searchQuery.toLowerCase()

        if (name?.includes(searchQuery)) {
          return true
        }
      } else {
        let firstName = (user?.firstName || '').toLowerCase()
        let lastName = (user?.lastName || '').toLowerCase()
        name = firstName + lastName
        if (name?.includes(searchQuery)) {
          return true
        }
      }
    }
    return false
  }

  const searchByBusinessName = useCallback((): boolean => {
    const users = Object.values(channel?.state?.members).map((member) => member.user) || []
    const businessNames = users.map((user) => user?.businessName || '') as string[]

    for (const business of businessNames) {
      if (business.toLowerCase().includes(searchChannel.toLowerCase())) return true
    }
    return false
  }, [channel?.state?.members, searchChannel])

  useEffect(() => {
    if (channel) {
      // If we have a search query, check if this channel has matching messages
      if (searchChannel.trim()) {
        // If we have matching messages for this channel, show it
        if (channelMatchingMessages && channelMatchingMessages.has(channel.cid)) {
          setIsChannelDisplay(true)
        } else {
          // Also check for user/business name matches as fallback
          const userIdFound = searchByUserId()
          const businessNameFound = searchByBusinessName()
          setIsChannelDisplay(userIdFound || businessNameFound)
        }
      } else {
        // No search query, show all channels
        setIsChannelDisplay(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchChannel, channelMatchingMessages])

  const getLastMessage = () => {
    try {
      // Show highlighted search preview if searching and there are results
      if (isSearching && searchChannel.trim() && channelMatchingMessages?.has(channel.cid)) {
        const messages = channelMatchingMessages.get(channel.cid) || []
        if (messages.length > 0) {
          const msg = messages[0]
          return <span>{highlightSearchTerm(msg.text || '', searchChannel)}</span>
        }
      }

      // Default behavior: show last message
      const messages = channel.state.messages
      if (messages && messages.length > 0) {
        const lastMessage = messages[messages.length - 1]

        // Handle different message types
        if (lastMessage.attachments && lastMessage.attachments.length > 0) {
          const attachment = lastMessage.attachments[0]
          if (attachment.type === 'image') {
            return <span>📷 Image</span>
          } else if (attachment.type === 'file') {
            return <span>📎 File</span>
          } else if (attachment.type === 'video') {
            return <span>🎥 Video</span>
          } else if (attachment.type === 'audio') {
            return <span>🎵 Audio</span>
          }
        }

        const text = lastMessage.text || ''
        const mentionedUsers = (lastMessage as any)?.mentioned_users || []

        // Render mentions as styled @Name in the preview
        if (text && Array.isArray(mentionedUsers) && mentionedUsers.length > 0) {
          const displayNames: string[] = mentionedUsers
            .map((u: any) => {
              const first = u?.firstName || u?.first_name || ''
              const last = u?.lastName || u?.last_name || ''
              const fallback = u?.name || ''
              const full = `${first} ${last}`.trim()
              return full || fallback
            })
            .filter(Boolean)

          // Escape regex special chars
          const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          const patterns = displayNames.map((n) => `@${escapeRegex(n)}`)
          const regex = patterns.length ? new RegExp(`(${patterns.join('|')})`, 'g') : null

          if (regex) {
            const parts = text.split(regex)
            return (
              <span>
                {parts.map((part, idx) => {
                  if (patterns.includes(part)) {
                    return (
                      <span key={idx} className="text-app-primary-80 font-medium">
                        {part}
                      </span>
                    )
                  }
                  return <span key={idx}>{part}</span>
                })}
              </span>
            )
          }
        }

        // Return text message or fallback
        return <span>{text || 'No recent activity'}</span>
      }
      return <span>No recent activity</span>
    } catch (err) {
      return <span>No recent activity</span>
    }
  }

  // Helper function to highlight search terms in the message
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return <span>{text}</span>

    try {
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')
      const parts = text.split(regex)

      return (
        <span>
          {parts.map((part, index) => {
            // Check if this part matches the search term (case insensitive)
            // Also check if the part is not empty to avoid highlighting empty strings
            const isMatch = part && part.toLowerCase() === searchTerm.toLowerCase()
            return isMatch ? (
              <span
                key={index}
                className="bg-app-primary-10 text-app-primary-80 rounded font-medium"
              >
                {part}
              </span>
            ) : (
              part && <span key={index}>{part}</span>
            )
          })}
        </span>
      )
    } catch (err) {
      return <span>{text}</span>
    }
  }

  return (
    <>
      {isChannelDisplay && (
        <div
          className={`relative cursor-pointer transition-colors duration-150 rounded-lg ${
            isActiveChannel && !showChannelCreate ? 'bg-app-grey-10' : 'hover:bg-app-grey-10'
          }`}
          onClick={async () => {
            if (setActiveChannel) {
              setShowChannelCreate(false)
              handleUnreadCounts()
              setActiveChannel(channel)
              removeUnreadMessage()

              await channel.markRead()
            }
          }}
        >
          <div className="flex items-center px-6 py-2.5 space-x-3">
            {/* Avatar Section */}
            <div className="flex-shrink-0 relative">
              {members.length > 1 ? (
                <div className="w-12 h-12 bg-app-grey-30 rounded-full flex items-center justify-center overflow-hidden">
                  {channel.data?.image ? (
                    <img
                      src={channel.data.image}
                      alt="Group icon"
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <svg
                      className="w-6 h-6 text-app-grey-70"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  )}
                </div>
              ) : (
                <AppAvatar
                  size="lg"
                  type="mixed"
                  shape="Rounded"
                  text={getInitials({
                    firstName: members[0]?.user?.firstName,
                    lastName: members[0]?.user?.lastName,
                  })}
                />
              )}

              {channelMessageCounts > 0 && (
                <span className="absolute -top-1 -right-1 z-10 w-5 h-5 bg-app-primary-60 text-app-white-100 text-xs font-medium rounded-full flex items-center justify-center">
                  {channelMessageCounts > 99 ? '99+' : channelMessageCounts}
                </span>
              )}
            </div>

            {/* Message Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <h4 className="text-sm font-medium text-app-black-100 truncate">
                  {channel.data?.name || (members && members.length > 0 && getChannelName(members))}
                </h4>
                <span className="text-xs text-app-grey-55 flex-shrink-0">
                  {/* timestamp placeholder */}
                </span>
              </div>

              <div className="text-xs text-app-grey-60 truncate">{getLastMessage()}</div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MessagingChannelPreview
